(ns app.front.view.committees.subs
  (:require [app.domain.committee.model :as committee-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.query.core :as query]
            [app.front.query.process :as process-query]
            [app.front.ui.dnd :as dnd]
            [clojure.set :as set]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]))

(defn show-add-handler [k]
  (fn [db [_ id]]
    (-> db
        (update ::status assoc-in [k id] :visible))))

(rf/reg-event-db ::show-add-committee
  (show-add-handler :add-committee))

(rf/reg-event-db ::show-add-meeting
  (show-add-handler :add-meeting))

(defn get-default-date-range []
  {:start (date-util/today-start-of-day)
   :end   (-> (date-util/tomorrow-start-of-day)
              (date-util/add-days-to-date 30))})

(defn hide-add-handler [k]
  (fn [db [_ id]]
    (-> db
        (update-in [::status k] dissoc id))))

(rf/reg-event-db ::hide-add-committee
  (hide-add-handler :add-committee))

(rf/reg-event-db ::hide-add-meeting
  (hide-add-handler :add-meeting))

(defn get-date-range [db & _]
  (->> db
       ::date-range
       (merge (get-default-date-range))))

(rf/reg-sub ::date-range
  get-date-range)

(rf/reg-event-db ::set-committee-id
  (fn [db [_ committee-id]]
    (-> db
        (assoc ::selected-committee-id committee-id))))

(rf/reg-sub ::selected-committee-id
  :-> ::selected-committee-id)

(rf/reg-sub ::selected-committee-status
  :<- [::selected-committee-id]
  (fn [id _]
    (<sub [::entities/entity-attr id :domain/state])))

(rf/reg-sub ::can-add-meeting?
  :<- [::selected-committee-status]
  (fn [status _]
    (= status :committee.state/active)))

(rf/reg-sub ::selected-committee
  :<- [::selected-committee-id]
  :<- [::entities/context]
  (fn [[id ctx] _]
    (entities/find-entity ctx id)))

(rf/reg-event-db ::set-pre-selected-group-id
  (fn [db [_ group-id]]
    (-> db
        (assoc ::pre-selected-group-id group-id))))

(rf/reg-sub ::pre-selected-group-id
  :-> ::pre-selected-group-id)

(rf/reg-event-fx ::create-committee-legacy
  (fn [{:keys [db]} [_ authority-group-id params]]
    {:db           (-> db
                       (update ::status assoc-in [:add-committee nil] :in-progress))
     :comm/command {:command    :committee/create-legacy
                    :data       (-> params
                                    (assoc :authority-group-id authority-group-id))
                    :on-success [::create-committee.success nil]
                    :notify     {:success :committee/committee-created}}}))

(rf/reg-event-fx ::create-committee.success
  (fn [{:keys [db]} [_ id status resp]]
    {:db       (-> db
                   (update ::status assoc-in [:add-committee id] :done))
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::create-meeting
  (fn [{:keys [db]} [_ params]]
    (let [origin (:origin params)]
      {:db           (-> db
                         (update ::status assoc-in [:add-meeting (::selected-committee-id db)] :in-progress))
       :comm/command {:command    :committee/create-meeting
                      :data       (dissoc params :origin)
                      :on-success [::create-meeting.success (::selected-committee-id db) (:committee-id params) origin]
                      :notify     {:success :committee/meeting-added}}})))

(rf/reg-event-fx ::create-meeting.success
  (fn [{:keys [db]} [_ id target-id origin status resp]]
    {:db         (-> db
                     (update ::status assoc-in [:add-meeting id] :done))
     :dispatch-n (cond-> [[::entities/apply-changes status resp]]
                   (= origin :meetings-view)
                   (conj [::hide-add-meeting nil])
                   (not= origin :meetings-view)
                   (conj [:route/replace {:route  :view/committee
                                          :params {:committee-id target-id}
                                          :query  (when-let [auth-group-id (::pre-selected-group-id db)]
                                                    {:group-id auth-group-id})}]))}))

(rf/reg-event-fx ::reorder-agenda
  (fn [_ [_ from to]]
    {:comm/command {:command    :committee/reorder-items
                    :data       {:items (dnd/ordering-tx-for-entity from to)}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-sub ::agenda-items
  (fn [[_ meeting-id]]
    (rf/subscribe [::entities/entities {:entity/parent meeting-id
                                        :entity/type   :entity.type/committee-item}]))
  :-> committee-model/sort-items)

(defn toggle [items item]
  (let [items (or items #{})]
    ((if (contains? items item) disj conj) items item)))

(rf/reg-event-db ::toggle-item-selected
  (interceptors/path ::state)
  (fn [db [_ item-id]]
    (-> db
        (update :selected-items toggle item-id))))

(rf/reg-event-db ::set-selection
  (interceptors/path ::state)
  (fn [state [_ new-selection]]
    (assoc state :selected-items new-selection)))

(rf/reg-sub ::selected-items
  :<- [::state]
  :-> :selected-items)

(rf/reg-sub ::none-selected?
  :<- [::selected-items]
  :-> empty?)

(rf/reg-sub ::all-selected?
  (fn [[_ meeting-id]]
    [(rf/subscribe [::selected-items])
     (rf/subscribe [::agenda-items meeting-id])])
  (fn [[selected items] _]
    (-> items du/entity-id-set (set/difference selected) empty?)))

(rf/reg-sub ::selected-actions
  :<- [::state]
  (fn [state]
    (:selected-actions state {})))

(def meeting-close-signals
  (->> committee-model/end-meeting-actions
       vals
       (keep :signal)
       (into #{})))

(rf/reg-sub ::selected-item-workspace-ids
  :<- [::selected-items]
  :<- [::entities/context]
  (fn [[item-ids ctx] _]
    (->> (entities/find-entities ctx item-ids)
         (map :committee-item/item)
         (map :workspace/id))))

(rf/reg-sub ::possible-actions-per-workspace
  :<- [::selected-item-workspace-ids]
  (fn [workspace-ids _]
    (<sub [::process-query/allowed-signals :entity.type/application workspace-ids])))

(rf/reg-sub ::possible-actions-for-selected
  :<- [::possible-actions-per-workspace]
  (fn [allowed-signals _]
    (let [possible-signals (->> allowed-signals
                                vals
                                (map set)
                                (apply set/union))]
      (->> committee-model/end-meeting-actions
           (filter (fn [[_ {:keys [signal]}]]
                     (or (nil? signal) (contains? possible-signals signal))))
           (map first)))))

(rf/reg-sub ::disabled-actions-for-selected
  :<- [::possible-actions-per-workspace]
  (fn [allowed-signals _]
    (let [signals-permitted-for-some (->> allowed-signals
                                          vals
                                          (map set)
                                          (apply set/union))
          signals-permitted-for-all  (->> allowed-signals
                                          vals
                                          (map set)
                                          (apply set/intersection))
          signals-to-show-disabled   (set/difference signals-permitted-for-some
                                                     signals-permitted-for-all)]
      (->> committee-model/end-meeting-actions
           (filter (fn [[_ {:keys [signal]}]]
                     (contains? signals-to-show-disabled signal)))
           (map first)
           set))))

(rf/reg-sub ::selected-action
  :<- [::selected-actions]
  (fn [selected-actions [_ item-id]]
    (get selected-actions item-id)))

(rf/reg-sub ::selected-action-text
  (fn [[_ item-id]]
    [(rf/subscribe [:tr])
     (rf/subscribe [::selected-action item-id])])
  (fn [[tr tr-kw] _]
    (when tr-kw
      (tr [tr-kw]))))

(rf/reg-sub ::selected-action-for-selection
  :<- [::selected-actions]
  :<- [::selected-items]
  (fn [[actions items] _]
    (let [selection (->> items
                         (reduce (fn [values item-id]
                                   (conj values (get actions item-id)))
                                 #{}))]
      (when-not (next selection) (first selection)))))

(rf/reg-event-db ::select-action
  (interceptors/path ::state)
  (fn [state [_ selected-item-ids action-def]]
    (->> selected-item-ids
         (reduce (fn [state item-id]
                   (assoc-in state [:selected-actions item-id] action-def))
                 state))))

(rf/reg-sub ::additional-complete-meeting-data
  :<- [::selected-actions]
  (fn [actions _]
    {:actions actions}))

(rf/reg-sub ::item-selected?
  :<- [::selected-items]
  (fn [selected-items [_ item-id]]
    (contains? selected-items item-id)))

(rf/reg-sub ::state
  :-> ::state)

(rf/reg-sub ::mode
  :<- [::state]
  (fn [state [_ meeting-id]]
    (get-in state [:mode meeting-id] :default)))

(rf/reg-event-db ::set-mode
  (interceptors/path ::state)
  (fn [state [_ meeting-id new-mode]]
    (-> state
        (dissoc :selected-actions
                :selected-items)
        (assoc :mode {meeting-id new-mode}))))

(defn handle-change-meeting [command]
  (fn [{:keys [db]} [_ meeting-id & [additional-command-data]]]
    {:db           (-> db
                       (update ::status assoc-in [:change-meeting command meeting-id] :in-progress))
     :comm/command {:command        command
                    :data           (merge (util/mmap meeting-id)
                                           additional-command-data)
                    :notify         {:success :committee/meeting-change-complete}
                    :on-success     [::change-meeting.success command meeting-id]
                    :on-bad-request [::change-meeting.error command meeting-id]}}))

(rf/reg-event-fx ::cancel-meeting
  (handle-change-meeting :committee/cancel-meeting))

(rf/reg-event-fx ::complete-meeting
  (handle-change-meeting :committee/complete-meeting))


(rf/reg-event-fx ::change-meeting.success
  (fn [{:keys [db]} [_ command meeting-id status resp]]
    {:db       (-> db
                   (update ::status assoc-in [:change-meeting command meeting-id] :done)
                   (update ::state dissoc :selected-actions :selected-items :mode))
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::change-meeting.error
  (fn [{:keys [db]} [_ command meeting-id _status body]]
    {:db       (-> db
                   (update ::status assoc-in [:change-meeting command meeting-id] :done))
     :dispatch [:notifications/add
                :toast
                :error
                (or (:error-title body) :error/error)
                (or (:error body) :error/unknown)]}))

(rf/reg-sub ::committees
  :<- [::entities/entities {:entity/type :entity.type/committee}]
  :<- [:language]
  :<- [::pre-selected-group-id]
  (fn [[committees language auth-group-id] _]
    (cond->> committees
      auth-group-id (filter #(= auth-group-id (entities/get-id % :entity/parent)))
      :always (sort-by #(committee-model/committee-name % language)))))

(rf/reg-sub ::committees-in-group
  (fn [[_ group-id _] _]
    [(rf/subscribe [::entities/entities {:entity/type :entity.type/committee :entity/parent group-id}])
     (rf/subscribe [:language])])
  (fn [[committees language] [_ _ active-only?]]
    (cond->> committees
      active-only? (filter du/is-active?)
      :always (sort-by #(committee-model/committee-name % language)))))

(defn next-meeting [committee]
  (let [next-meeting (->> committee
                          :entity/_parent
                          (filter (du/in-state-fn :committee-meeting.state/active))
                          (sort-by :committee-meeting/date)
                          first)
        n-workspaces (-> next-meeting :entity/_parent count)]
    (when next-meeting
      (merge
        (select-keys committee [:db/id :committee/name :committee/names])
        (select-keys next-meeting [:committee-meeting/date])
        {:item-count n-workspaces}))))

(rf/reg-sub ::next-meetings
  :<- [::committees]
  (fn [committees _]
    (->> committees
         (keep next-meeting))))

(defn select-date-handler [k]
  (fn [{:keys [db]} [_ date]]
    (let [db (-> db
                 (assoc-in [::date-range k] date))]
      (merge {:db db}
             (when date
               ;; Only dispatch when it's a valid date, because the query API errors otherwise
               {:dispatch [::query/fetch-once
                           :committee/meetings
                           (merge {:committee-id (::selected-committee-id db)}
                                  (get-date-range db))]})))))

(rf/reg-event-fx ::select-date-range-end
  (select-date-handler :end))

(rf/reg-event-fx ::select-date-range-start
  (select-date-handler :start))

(rf/reg-sub ::date-range-end
  :<- [::date-range]
  :-> :end)

(rf/reg-sub ::date-range-start
  :<- [::date-range]
  :-> :start)

(def default-visible-meeting-states
  (set committee-model/committee-states))

(rf/reg-sub ::all-meeting-states
  :-> ::meeting-states)

(rf/reg-sub ::meeting-states
  :<- [::all-meeting-states]
  (fn [all-states [_ committee-id]]
    (get all-states committee-id default-visible-meeting-states)))

(rf/reg-sub ::meetings
  (fn [[_ committee-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::meeting-states committee-id])
     (rf/subscribe [::date-range])])
  (fn [[ctx states {:keys [start end]}] [_ committee-id]]
    (->> {:entity/parent committee-id
          :entity/type   :entity.type/committee-meeting}
         (entities/find-entities ctx)
         (filter (apply du/in-state-fn states))
         (filter #(-> % :committee-meeting/date (date-util/before? end)))
         (filter #(-> % :committee-meeting/date (date-util/after? start)))
         (sort-by :committee-meeting/date))))

(rf/reg-sub ::statuses
  :-> ::status)

(rf/reg-sub ::add-committee-status
  :<- [::statuses]
  (fn [status _]
    (-> status :add-committee (get nil))))

(rf/reg-sub ::status
  :<- [::statuses]
  (fn [status [_ & path]]
    (get-in status path)))

(rf/reg-sub ::add-meeting-status
  (fn [[_ id]]
    (rf/subscribe [::status :add-meeting id]))
  :-> identity)

(rf/reg-sub ::meeting-change-loading?
  (fn [[_ command meeting-id]]
    (rf/subscribe [::status :change-meeting command meeting-id]))
  (fn [status _]
    (= status :in-progress)))

(rf/reg-sub ::edit-item-status
  (fn [[_ id]]
    (rf/subscribe [::status :edit-item id]))
  :-> identity)

(defn set-edit-status [db item-id status]
  (-> db
      (update ::status assoc-in [:edit-item item-id] status)))

(rf/reg-event-db ::set-edit-item-status
  (fn [db [_ committee-id status]]
    (-> db
        (set-edit-status committee-id status))))


(rf/reg-event-fx ::edit-committee-legacy
  (fn [{:keys [db]} [_ committee]]
    {:db           (-> db
                       (set-edit-status (:db/id committee) :in-progress))
     :comm/command {:command    :committee/edit-legacy
                    :data       committee
                    :notify     {:success :committee/committee-updated}
                    :on-success [::edit-committee.success (:db/id committee)]}}))

(rf/reg-event-fx ::edit-committee.success
  (fn [{:keys [db]} [_ committee-id status resp]]
    {:db       (-> db
                   (set-edit-status committee-id :done))
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::delete-committee-legacy
  (fn [_ [_ committee-id]]
    {:comm/command {:command    :committee/delete-legacy
                    :data       (util/mmap committee-id)
                    :notify     {:success :committee/committee-deleted}
                    :on-success [::delete.success]}}))

(rf/reg-event-fx ::delete.success
  (fn [{:keys [db]} [_ committee-id status resp]]
    {:db       (-> db
                   (update ::retractable-ids
                           set/union
                           (-> resp :retractable-id util/->set))
                   (set-edit-status committee-id nil)
                   (update ::edit-meeting-type-form dissoc committee-id))
     :dispatch [::entities/apply-changes status (:changes resp)]}))

(rf/reg-event-fx ::fetch-committees
  (fn [_ [_ group-id]]
    {:comm/command {:command    :committee/committees
                    :data       (when group-id
                                  (util/mmap group-id))
                    :on-success [::fetch-committees.success]}}))

(rf/reg-event-fx ::fetch-committees.success
  (fn [{:keys [db]} [_ status resp]]
    {:fx [[:dispatch [::entities/merge-entity-db status (:entities resp)]]]
     :db (p/assoc-if db ::retractable-ids (:retractable-ids resp))}))



(rf/reg-sub ::retractable-ids :-> ::retractable-ids)

(rf/reg-sub ::retractable?
  :<- [::retractable-ids]
  (fn [retractable-ids [_ committee-id]]
    (contains? retractable-ids committee-id)))

(rf/reg-event-fx ::retract-committee-legacy
  (fn [_ [_ committee-id]]
    {:comm/command {:command    :committee/retract-legacy
                    :data       (util/mmap committee-id)
                    :notify     {:success :committee/retracted}
                    :on-success [::entities/apply-changes]}}))

;;; Domain-level committee management events
;;; These events use the domain-level API endpoints when the
;;; :feature-flag.id/domain-level-meetings flag is enabled.


(rf/reg-event-fx ::edit-committee
  (fn [{:keys [db]} [_ committee]]
    {:db           (-> db
                       (set-edit-status (:db/id committee) :in-progress))
     :comm/command {:command    :committee/edit
                    :data       committee
                    :notify     {:success :meeting-type/updated}
                    :on-success [::edit-committee.success (:db/id committee)]}}))

(rf/reg-event-fx ::delete-committee
  (fn [_ [_ committee-id]]
    {:comm/command {:command    :committee/delete
                    :data       (util/mmap committee-id)
                    :notify     {:success :meeting-type/deactivated}
                    :on-success [::delete.success committee-id]}}))

(rf/reg-event-fx ::activate-committee
  (fn [_ [_ committee-id]]
    {:comm/command {:command    :committee/edit
                    :data       {:db/id        committee-id
                                 :domain/state :committee.state/active}
                    :notify     {:success :meeting-type/activated}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-fx ::retract-committee
  (fn [_ [_ committee-id]]
    {:comm/command {:command    :committee/retract
                    :data       (util/mmap committee-id)
                    :notify     {:success :meeting-type/retracted}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-db ::set-editing-item
  (interceptors/path ::state)
  (fn [state [_ item-id]]
    (assoc state :editing-item-id item-id)))

(rf/reg-event-db ::clear-editing-item
  (interceptors/path ::state)
  (fn [state _]
    (dissoc state :editing-item-id)))

(rf/reg-sub ::editing-item-id
  :<- [::state]
  :-> :editing-item-id)

(rf/reg-sub ::item-workspace-deficiencies
  :<- [::entities/context]
  (fn [ctx [_ item-id]]
    (let [item      (entities/find-entity ctx item-id)
          workspace (:committee-item/item item)]
      (when-let [execution (workspace-model/workspace-execution workspace)]
        (->> (inspection-model/phase-inspections execution)
             (mapcat inspection-model/deficiencies)
             (filter (du/in-state-fn :inspection.deficiency.state/active)))))))

;; Meeting Type Subs
(rf/reg-sub ::add-meeting-type-status
  (fn [[_ group-id]]
    (rf/subscribe [::status :add-meeting-type group-id]))
  :-> identity)

(rf/reg-sub ::available-authority-groups
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entity group-id]))
  (fn [group _]
    (->> (group-model/descendant-authority-groups group)
         (filter #(or (group-model/has-feature? % :group.feature/committee-meetings)
                      (->> %
                           group-model/get-permit-types
                           (set/intersection ptc-model/permit-types-with-committees)
                           seq))))))

(rf/reg-sub ::available-meeting-agenda-templates
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:domain/authority-group group-id
                                        :entity/type            :entity.type/meeting-agenda-template}]))
  (fn [templates _]
    (->> templates
         (filter #(du/in-state? :meeting-agenda-template.state/active %))
         (sort-by :meeting-agenda-template/name))))

(rf/reg-sub ::available-package-templates
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:domain/authority-group group-id
                                        :entity/type            :entity.type/package-pdf-template}]))
  (fn [templates _]
    (->> templates
         (remove #(du/in-state? :package-pdf-template.state/disabled %)))))

(rf/reg-sub ::meeting-type-form-params
  (fn [db [_ group-id]]
    (get-in db [::meeting-type-form group-id])))

(rf/reg-event-db ::show-add-meeting-type
  (fn [db [_ group-id]]
    (-> db
        (update ::status assoc-in [:add-meeting-type group-id] :visible)
        (assoc-in [::meeting-type-form group-id] {}))))

(rf/reg-event-db ::hide-add-meeting-type
  (fn [db [_ group-id]]
    (-> db
        (update-in [::status :add-meeting-type] dissoc group-id)
        (update ::meeting-type-form dissoc group-id))))

(rf/reg-event-db ::set-meeting-type-field
  (fn [db [_ group-id field value]]
    (assoc-in db [::meeting-type-form group-id field] value)))


(rf/reg-event-fx ::create-meeting-type
  (fn [{:keys [db]} [_ group-id params]]
    {:db           (-> db
                       (update ::status assoc-in [:add-meeting-type group-id] :in-progress))
     :comm/command {:command    :committee/create
                    :data       (-> params
                                    (assoc :domain-group-id group-id))
                    :on-success [::create-meeting-type.success group-id]
                    :notify     {:success :meeting-type/created}}}))

(rf/reg-event-fx ::create-meeting-type.success
  (fn [{:keys [db]} [_ group-id status resp]]
    {:db         (-> db
                     (update ::status assoc-in [:add-meeting-type group-id] :done)
                     (update ::meeting-type-form dissoc group-id))
     :dispatch-n [[::entities/apply-changes status resp]
                  [::fetch-committees group-id]]}))

(rf/reg-sub ::edit-meeting-type-form-params
  (fn [db [_ committee-id]]
    (get-in db [::edit-meeting-type-form committee-id])))

(rf/reg-event-db ::init-edit-meeting-type-form
  (fn [db [_ committee-id committee lang]]
    (let [{:keys [:committee/meeting-agenda-template-ref
                  :committee/packet-template
                  :committee/schedule-day-of-week
                  :committee/schedule-frequency
                  :committee/schedule-period
                  :committee/participants
                  :committee/authority-groups]}          committee
          {:keys [name-attr
                  description-attr
                  agenda-template-attr
                  packet-template-attr
                  start-day-attr
                  repeat-count-attr
                  frequency-attr
                  participants-attr
                  authority-groups-attr]}                {:name-attr             :committee/name
                                                          :description-attr      :committee/description
                                                          :agenda-template-attr  :committee/agenda-template
                                                          :packet-template-attr  :committee/packet-template
                                                          :start-day-attr        :committee/schedule-day-of-week
                                                          :repeat-count-attr     :committee/schedule-frequency
                                                          :frequency-attr        :committee/schedule-period
                                                          :participants-attr     :committee/participants
                                                          :authority-groups-attr :committee/authority-groups}
          extracted-ids                                  (or (->> authority-groups (map :db/id) set) #{})]
      (assoc-in db [::edit-meeting-type-form committee-id]
                {name-attr             (committee-model/committee-name committee lang)
                 description-attr      (committee-model/committee-description committee lang)
                 agenda-template-attr  (-> meeting-agenda-template-ref :db/id)
                 packet-template-attr  (-> packet-template :db/id)
                 start-day-attr        schedule-day-of-week
                 repeat-count-attr     schedule-frequency
                 frequency-attr        schedule-period
                 participants-attr     (or participants [])
                 authority-groups-attr extracted-ids}))))

(rf/reg-event-db ::set-edit-meeting-type-field
  (fn [db [_ committee-id field value]]
    (assoc-in db [::edit-meeting-type-form committee-id field] value)))


(rf/reg-event-db ::clear-edit-meeting-type-form
  (fn [db [_ committee-id]]
    (update db ::edit-meeting-type-form dissoc committee-id)))
